<template>
  <div :class="['result-card', customClass]" @click.stop="toggleExpanded($event)">
    <!-- Main info always visible -->
    <h5>{{ result.huntName.huntName }}</h5>
    <p>
      <strong>Unit:</strong> {{ result.huntName.unitName }} ({{ result.huntName.unitCode }}) <br />
      <strong>Weapon:</strong> {{ result.huntName.weapon }} <br />
      <strong :style="{ color: getOddsColor(result.drawOdds.totalPermitOdds), fontWeight: 'bold' }">Projected Total Odds: {{ formatOdds(result.drawOdds.totalPermitOdds) }}</strong><br />
      <strong>Projected # of Other Applicants:</strong> {{
        result.drawOdds.projectedNumberOfOtherApplicants
      }}
    </p>

    <!-- Save Hunt Icon -->
    <span class="save-icon" @click.prevent.stop="toggleSave">
      <i :class="[isHuntSaved ? 'mdi mdi-bookmark' : 'mdi mdi-bookmark-outline']"></i>
    </span>

    <!-- Collapse/Expand Icon -->
    <span class="toggle-icon">
      <i :class="[isExpanded ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down']"></i>
    </span>

    <!-- Use a transition for a smoother show/hide -->
    <transition name="fade">
      <div v-if="isExpanded && hasActiveSubscription" class="extended-info">
        <!-- Draw Odds Details -->
        <div class="section">
          <h6>Draw Odds Details</h6>
          <p>
            <strong>Projected Bonus Permit Odds:</strong> {{ formatOdds(result.drawOdds.bonusPermitOdds) }}<br />
            <strong>Projected Regular Permit Odds:</strong> {{ formatOdds(result.drawOdds.regularPermitOdds) }}<br />
            <strong>Projected Points Needed:</strong> {{
              result.drawOdds.projectedPointsRequiredForPermit
            }}<br />
            <strong>Projected Bonus Permits Available:</strong> {{
              result.drawOdds.projectedBonusPermitsAvailable
            }}<br />
            <strong>Projected Regular Permits Available:</strong> {{
              result.drawOdds.projectedRegularPermitsAvailable
            }}
          </p>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="loading-state">
          Loading hunt details...
        </div>

        <!-- Hunt Details -->
        <div v-if="huntDetails && !isLoading" class="hunt-details">
          <!-- Source Header -->
          <div class="source-header">
            <a href="https://dwrapps.utah.gov/huntboundary/" target="_blank" rel="noopener noreferrer">
              (Sourced from the public Utah Hunt Planner)
            </a>
          </div>
          <!-- Success Rate -->
          <div class="section" v-if="successRate">
            <h6>Previous Year Success Rate</h6>
            <p>{{ successRate }}%</p>
          </div>

          <!-- General Information -->
          <div class="section" v-if="huntDetails.GENERAL_INFORMATION">
            <h6>General Information</h6>
            <p v-html="huntDetails.GENERAL_INFORMATION"></p>
          </div>

          <!-- Biologist Notes -->
          <div class="section" v-if="huntDetails.BIOLOGIST_NOTES">
            <h6>Biologist Notes</h6>
            <p v-html="huntDetails.BIOLOGIST_NOTES"></p>
          </div>

          <!-- Weather Considerations -->
          <div class="section" v-if="huntDetails.WEATHER_CONS">
            <h6>Weather Considerations</h6>
            <p v-html="huntDetails.WEATHER_CONS"></p>
          </div>

          <!-- Safety Considerations -->
          <div class="section" v-if="huntDetails.SAFETY_CONS">
            <h6>Safety Considerations</h6>
            <p v-html="huntDetails.SAFETY_CONS"></p>
          </div>

          <!-- Management Objectives -->
          <div class="section" v-if="huntDetails.MANAGEMENT_OBJ">
            <h6>Management Objectives</h6>
            <p v-html="formattedManagementObj"></p>
          </div>
        </div>
      </div>
    </transition>

    <!-- Premium Notice for non-subscribers -->
    <transition name="fade">
      <div v-if="showPremiumNotice" class="premium-notice">
        <div class="premium-notice-content">
          <i class="mdi mdi-crown premium-icon"></i>
          <p><strong>Premium Feature</strong></p>
          <p>Detailed draw odds breakdown and hunt information are available with a premium account.</p>
          <button class="btn btn-sm btn-primary" @click.stop="showUpgradeModal">
            Upgrade to Premium
          </button>
        </div>
      </div>
    </transition>

    <!-- Premium Upgrade Modal -->
    <PremiumUpgrade 
      v-if="showUpgrade" 
      @close="showUpgrade = false"
    />
  </div>
</template>

<script>
import "@mdi/font/css/materialdesignicons.css";
import { mapState, mapGetters } from 'vuex';
import PremiumUpgrade from './PremiumUpgrade.vue';

export default {
  name: "DrawOddsResultCard",
  components: {
    PremiumUpgrade,
  },
  props: {
    result: {
      type: Object,
      required: true,
    },
    customClass: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      isExpanded: false,
      huntDetails: null,
      isLoading: false,
      showUpgrade: false,
    };
  },
  computed: {
    ...mapState({
      savedHunts: state => state.savedHunts,
      isAuthenticated: state => state.isAuthenticated
    }),
    ...mapGetters(['hasActiveSubscription']),
    showPremiumNotice() {
      return this.isExpanded && !this.hasActiveSubscription;
    },
    isHuntSaved() {
      const huntId = this.result.huntName.id;
      const isSaved = this.savedHunts.includes(huntId);
      // console.log(`Hunt ${huntId} saved status:`, isSaved);
      return isSaved;
    },
    successRate() {
      if (!this.huntDetails?.MANAGEMENT_OBJ) return null;
      const parts = this.huntDetails.MANAGEMENT_OBJ.split('~');
      return parts[0] ? parseFloat(parts[0]).toFixed(1) : null;
    },
    formattedManagementObj() {
      if (!this.huntDetails?.MANAGEMENT_OBJ) return '';
      // Remove the success rate from the beginning and split by ~
      const parts = this.huntDetails.MANAGEMENT_OBJ.split('~');
      // The last part often contains a link, which we want to preserve as HTML
      const lastPart = parts[parts.length - 1];
      // Join the remaining parts with commas, excluding empty strings
      const middleParts = parts.slice(1, -1)
        .filter(part => part.trim())
        .join(', ');
      return middleParts + (lastPart ? '<br><br>' + lastPart : '');
    }
  },
  methods: {
    async toggleSave(event) {
      event.stopPropagation();
      event.preventDefault();
      console.log('Authentication state:', this.isAuthenticated);
      if (!this.isAuthenticated) {
        this.$emit('error', 'Please sign in to save hunts');
        return;
      }
      console.log('Hunt data:', this.result.huntName);
      const huntId = this.result.huntName.id;
      console.log('Toggling save for hunt:', huntId);
      // Toggle the saved state in Vuex
      try {
        const isSaved = this.savedHunts.includes(huntId);
        const response = await fetch(process.env.VUE_APP_API_BASE_URL + `/api/saved-hunts/hunt/${huntId}`, {
          method: isSaved ? 'DELETE' : 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          if (isSaved) {
            console.log('Removing hunt from saved:', huntId);
            this.$store.commit('removeSavedHunt', huntId);
          } else {
            console.log('Adding hunt to saved:', huntId);
            this.$store.commit('addSavedHunt', huntId);
          }
        } else {
          console.error('Failed to toggle saved hunt:', response.status, response.statusText);
          const text = await response.text();
          console.error('Response body:', text);
          this.$emit('error', 'Failed to save hunt');
        }
      } catch (error) {
        console.error('Error toggling saved hunt:', error);
        this.$emit('error', 'Failed to save hunt');
      }
    },
    async toggleExpanded(event) {
      // Check if there's any selected text
      const selectedText = window.getSelection().toString();
      if (selectedText) {
        return; // Don't toggle if text is selected
      }

      this.isExpanded = !this.isExpanded;
      
      // Emit event so parent components can adjust container height
      this.$emit('toggle-expanded', this.isExpanded);
      
      if (this.isExpanded && !this.huntDetails) {
        this.isLoading = true;
        try {
          const response = await fetch(process.env.VUE_APP_API_BASE_URL + `/api/hunt/id/${this.result.huntName.unitCode}`, {
          method: 'GET',
          credentials: 'include',
        });
          if (!response.ok) throw new Error('Failed to fetch hunt details');
          this.huntDetails = await response.json();
        } catch (error) {
          console.error('Error fetching hunt details:', error);
          this.$emit('error', 'Failed to load hunt details');
        } finally {
          this.isLoading = false;
        }
      }
    },
    getOddsColor(totalPermitOdds) {
      if (totalPermitOdds <= 0.5) {
        return 'red';
      } else if (totalPermitOdds <= 0.75) {
        return 'darkorange';
      } else {
        return 'darkgreen';
      }
    },
    formatOdds(odds) {
      odds = Math.min(odds, 1);
      return `${(odds * 100).toFixed(2)}%`;
    },

    showUpgradeModal() {
      this.showUpgrade = true;
    }
  },
};
</script>

<style scoped>
.result-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 8px;
  position: relative;
}

.result-card:hover {
  background-color: #ececec;
}

.result-card:active {
  background-color: #e0e0e0;
}

.even-card {
  background-color: #f9f9f9;
}

.odd-card {
  background-color: #fff;
}

.result-card p {
  padding: 0;
  margin-bottom: 10px;
}

/* Add right padding to h5 to prevent overlap with icons */
.result-card h5 {
  padding-right: 70px; /* Make room for both icons */
  margin-right: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.save-icon {
  position: absolute;
  top: 10px;
  right: 45px;
  cursor: pointer;
  font-size: 24px;
  color: #007bff;
  z-index: 1;
}

.save-icon:hover {
  color: #0056b3;
}

.toggle-icon {
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  font-size: 24px;
  color: #007bff;
}

.toggle-icon:hover {
  color: #0056b3;
}

.extended-info {
  margin-top: 15px;
  border-top: 1px solid #eee;
  padding-top: 15px;
}

.section {
  margin-bottom: 20px;
}

.section h6 {
  color: #2c3e50;
  margin-bottom: 8px;
  font-weight: 600;
}

.loading-state {
  text-align: center;
  padding: 20px;
  color: #666;
}

.hunt-details {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  margin-top: 15px;
}

.source-header {
  text-align: right;
  margin-bottom: 15px;
  font-size: 0.9em;
  color: #6c757d;
}

.source-header a {
  color: #6c757d;
  text-decoration: none;
}

.source-header a:hover {
  color: #007bff;
  text-decoration: underline;
}

/* Premium Notice Styles */
.premium-notice {
  margin-top: 15px;
  border-top: 1px solid #eee;
  padding-top: 15px;
}

.premium-notice-content {
  background-color: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.premium-icon {
  font-size: 2.5rem;
  color: #ffd700;
  margin-bottom: 10px;
  display: block;
}

.premium-notice-content p {
  margin-bottom: 10px;
  color: #495057;
}

.premium-notice-content p:first-of-type {
  color: #343a40;
  font-size: 1.1rem;
}

.premium-notice-content .btn {
  margin-top: 10px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.premium-notice-content .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

/* Simple fade transition classes */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
